import { useState } from "react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { LoadingState } from "../components/common/LoadingState";
import { ErrorState } from "../components/common/ErrorState";
import { PageHeader } from "../components/common/PageHeader";
import { useDataFetching } from "../hooks/useDataFetching";
import { analyticsAPI, schoolsAPI, type AnalyticsQuery } from "../api";
import { googleAnalyticsAPI } from "../api/services/googleAnalyticsService";
import { formatNumber } from "../utils/formatters";
import {
  Users,
  MessageSquare,
  Eye,
  TrendingUp,
  Clock,
  Activity,
  Shield,
  UserPlus,
  Globe,
  Monitor,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  SessionTrendChart,
  UserGrowthChart,
  EngagementFunnelChart,
  RetentionCohortChart,
  ConnectionDistributionChart,
  ExportButton,
  RefreshButton,
} from "../components/analytics";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";

export function EngagementPage() {
  const [period, setPeriod] = useState<'week' | 'month' | 'all'>('month');
  const [schoolId, setSchoolId] = useState<string | undefined>(undefined);

  const queryParams: AnalyticsQuery = {
    period,
    ...(schoolId && { school_id: schoolId }),
  };

  // Fetch all schools for filter
  const { data: schoolsData } = useDataFetching(['schools'], {
    queryFn: schoolsAPI.getAllSchools,
  });
  const schools = Array.isArray(schoolsData) ? schoolsData : [];

  // Fetch analytics data
  const {
    data: sessionData,
    isLoading: sessionLoading,
    error: sessionError,
  } = useDataFetching(['analytics', 'session', JSON.stringify(queryParams)], {
    queryFn: () => analyticsAPI.getSessionAnalytics(queryParams),
  });

  const {
    data: messagingData,
    isLoading: messagingLoading,
    error: messagingError,
  } = useDataFetching(['analytics', 'messaging', JSON.stringify(queryParams)], {
    queryFn: () => analyticsAPI.getMessagingAnalytics(queryParams),
  });

  const {
    data: profileViewsData,
    isLoading: profileViewsLoading,
    error: profileViewsError,
  } = useDataFetching(['analytics', 'profileViews', JSON.stringify(queryParams)], {
    queryFn: () => analyticsAPI.getProfileViewsAnalytics(queryParams),
  });

  const {
    data: engagementData,
    isLoading: engagementLoading,
    error: engagementError,
  } = useDataFetching(['analytics', 'engagement', JSON.stringify(queryParams)], {
    queryFn: () => analyticsAPI.getEngagementAnalytics(queryParams),
  });

  const {
    data: lifecycleData,
    isLoading: lifecycleLoading,
    error: lifecycleError,
  } = useDataFetching(['analytics', 'lifecycle', JSON.stringify(queryParams)], {
    queryFn: () => analyticsAPI.getUserLifecycleAnalytics(queryParams),
  });

  const {
    data: socialGraphData,
    isLoading: socialGraphLoading,
    error: socialGraphError,
  } = useDataFetching(['analytics', 'socialGraph', JSON.stringify(queryParams)], {
    queryFn: () => analyticsAPI.getSocialGraphAnalytics(queryParams),
  });

  const {
    data: referralsData,
    isLoading: referralsLoading,
    error: referralsError,
  } = useDataFetching(['analytics', 'referrals', JSON.stringify(queryParams)], {
    queryFn: () => analyticsAPI.getReferralsAnalytics(queryParams),
  });

  const {
    data: safetyData,
    isLoading: safetyLoading,
    error: safetyError,
  } = useDataFetching(['analytics', 'safety', JSON.stringify(queryParams)], {
    queryFn: () => analyticsAPI.getSafetyAnalytics(queryParams),
  });

  // Google Analytics data (maps period to GA date ranges)
  const gaDateRange = period === 'week' ? 'LAST_7_DAYS' : period === 'month' ? 'LAST_30_DAYS' : 'LAST_90_DAYS';

  const { data: gaUserData } = useDataFetching(['ga-users', gaDateRange], {
    queryFn: () => googleAnalyticsAPI.getUserMetrics({ dateRange: gaDateRange }),
    enabled: false, // Only fetch when on Web Analytics tab
  });

  const { data: gaSessionData } = useDataFetching(['ga-sessions', gaDateRange], {
    queryFn: () => googleAnalyticsAPI.getSessionMetrics({ dateRange: gaDateRange }),
    enabled: false,
  });

  const { data: gaPageData } = useDataFetching(['ga-pages', gaDateRange], {
    queryFn: () => googleAnalyticsAPI.getPageMetrics({ dateRange: gaDateRange }),
    enabled: false,
  });

  const { data: gaAcquisitionData } = useDataFetching(['ga-acquisition', gaDateRange], {
    queryFn: () => googleAnalyticsAPI.getAcquisitionMetrics({ dateRange: gaDateRange }),
    enabled: false,
  });

  const isLoading =
    sessionLoading ||
    messagingLoading ||
    profileViewsLoading ||
    engagementLoading ||
    lifecycleLoading ||
    socialGraphLoading ||
    referralsLoading ||
    safetyLoading;

  const hasError =
    sessionError ||
    messagingError ||
    profileViewsError ||
    engagementError ||
    lifecycleError ||
    socialGraphError ||
    referralsError ||
    safetyError;

  if (isLoading) {
    return (
      <DashboardLayout>
        <LoadingState type="dashboard" />
      </DashboardLayout>
    );
  }

  if (hasError) {
    return (
      <DashboardLayout>
        <ErrorState
          title="Error loading analytics"
          message="Please try refreshing the page or contact support if the issue persists."
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="responsive-container">
        <div className="space-y-6">
          <PageHeader
            title="Engagement Analytics"
            description="Track user engagement, activity, and platform health metrics"
          />

          {/* Filters and Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="w-full sm:w-48">
                <Select value={period} onValueChange={(value: string) => setPeriod(value as 'week' | 'month' | 'all')}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="week">Last Week</SelectItem>
                    <SelectItem value="month">Last Month</SelectItem>
                    <SelectItem value="all">All Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="w-full sm:w-64">
                <Select
                  value={schoolId || "all"}
                  onValueChange={(value) => setSchoolId(value === "all" ? undefined : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select school" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Schools</SelectItem>
                    {schools.map((school: { _id: string; school_name?: string; display_name?: string; name?: string }) => (
                      <SelectItem key={school._id} value={school._id}>
                        {school.school_name || school.display_name || school.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-2">
              <RefreshButton
                queryKeys={[
                  ['analytics', 'session', JSON.stringify(queryParams)],
                  ['analytics', 'messaging', JSON.stringify(queryParams)],
                  ['analytics', 'profileViews', JSON.stringify(queryParams)],
                  ['analytics', 'engagement', JSON.stringify(queryParams)],
                  ['analytics', 'lifecycle', JSON.stringify(queryParams)],
                  ['analytics', 'socialGraph', JSON.stringify(queryParams)],
                  ['analytics', 'referrals', JSON.stringify(queryParams)],
                  ['analytics', 'safety', JSON.stringify(queryParams)],
                ]}
              />
              <ExportButton
                data={{
                  session: sessionData,
                  messaging: messagingData,
                  profileViews: profileViewsData,
                  engagement: engagementData,
                  lifecycle: lifecycleData,
                  socialGraph: socialGraphData,
                  referrals: referralsData,
                  safety: safetyData,
                }}
                filename={`analytics-${period}-${new Date().toISOString().split('T')[0]}`}
              />
            </div>
          </div>

          {/* Analytics Tabs */}
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-5">
              <TabsTrigger value="overview">App Overview</TabsTrigger>
              <TabsTrigger value="engagement">Engagement</TabsTrigger>
              <TabsTrigger value="growth">Growth</TabsTrigger>
              <TabsTrigger value="safety">Safety</TabsTrigger>
              <TabsTrigger value="web">Web Analytics</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              {/* Session Metrics */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Session Analytics</h3>
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                  <MetricCard
                    title="Daily Active Users"
                    value={formatNumber(sessionData?.dailyActiveUsers || 0)}
                    icon={<Users className="h-4 w-4" />}
                    variant="blue"
                  />
                  <MetricCard
                    title="Weekly Active Users"
                    value={formatNumber(sessionData?.weeklyActiveUsers || 0)}
                    icon={<Users className="h-4 w-4" />}
                    variant="green"
                  />
                  <MetricCard
                    title="Monthly Active Users"
                    value={formatNumber(sessionData?.monthlyActiveUsers || 0)}
                    icon={<Users className="h-4 w-4" />}
                    variant="purple"
                  />
                  <MetricCard
                    title="Avg Session Duration"
                    value={`${Math.round((sessionData?.averageSessionDuration || 0) / 60)}m`}
                    description={`${formatNumber(sessionData?.totalSessions || 0)} total sessions`}
                    icon={<Clock className="h-4 w-4" />}
                    variant="amber"
                  />
                </div>
              </div>

              {/* Messaging Metrics */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Messaging Analytics</h3>
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                  <MetricCard
                    title="Messages Sent"
                    value={formatNumber(messagingData?.totalMessagesSent || 0)}
                    icon={<MessageSquare className="h-4 w-4" />}
                    variant="blue"
                  />
                  <MetricCard
                    title="Active Conversations"
                    value={formatNumber(messagingData?.activeConversations || 0)}
                    icon={<MessageSquare className="h-4 w-4" />}
                    variant="green"
                  />
                  <MetricCard
                    title="Reply Rate"
                    value={`${messagingData?.replyRate?.toFixed(1) || 0}%`}
                    icon={<Activity className="h-4 w-4" />}
                    variant="purple"
                  />
                  <MetricCard
                    title="Avg Response Time"
                    value={`${Math.round(messagingData?.averageResponseTimeMinutes || 0)}m`}
                    icon={<Clock className="h-4 w-4" />}
                    variant="amber"
                  />
                </div>
              </div>

              {/* Profile Views */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Profile Engagement</h3>
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  <MetricCard
                    title="Total Profile Views"
                    value={formatNumber(profileViewsData?.totalProfileViews || 0)}
                    icon={<Eye className="h-4 w-4" />}
                    variant="blue"
                  />
                  <MetricCard
                    title="Unique Viewers"
                    value={formatNumber(profileViewsData?.uniqueViewers || 0)}
                    icon={<Users className="h-4 w-4" />}
                    variant="green"
                  />
                  <MetricCard
                    title="View to Connection"
                    value={`${profileViewsData?.viewToConnectionRate?.toFixed(1) || 0}%`}
                    description="Conversion rate"
                    icon={<TrendingUp className="h-4 w-4" />}
                    variant="purple"
                  />
                </div>
              </div>

              {/* Peak Hours Chart */}
              {sessionData?.peakHours && sessionData.peakHours.length > 0 && (
                <SessionTrendChart data={sessionData.peakHours} />
              )}

              {/* User Growth Chart */}
              {sessionData && (
                <UserGrowthChart
                  dailyActiveUsers={sessionData.dailyActiveUsers}
                  weeklyActiveUsers={sessionData.weeklyActiveUsers}
                  monthlyActiveUsers={sessionData.monthlyActiveUsers}
                />
              )}
            </TabsContent>

            {/* Engagement Tab - Redesigned */}
            <TabsContent value="engagement" className="space-y-6">
              {/* Engagement Overview */}
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                <MetricCard
                  title="Total Events"
                  value={formatNumber(engagementData?.events?.totalViewed || 0)}
                  description={`${engagementData?.events?.totalAttendances || 0} attendances`}
                  icon={<Activity className="h-4 w-4" />}
                  variant="blue"
                />
                <MetricCard
                  title="Total Ideas"
                  value={formatNumber(engagementData?.ideas?.totalViewed || 0)}
                  description={`${engagementData?.ideas?.totalInterested || 0} interested`}
                  icon={<TrendingUp className="h-4 w-4" />}
                  variant="purple"
                />
                <MetricCard
                  title="Total Spaces"
                  value={formatNumber(engagementData?.spaces?.totalViewed || 0)}
                  description={`${engagementData?.spaces?.totalJoins || 0} members`}
                  icon={<Users className="h-4 w-4" />}
                  variant="green"
                />
                <MetricCard
                  title="Avg Conversion"
                  value={`${(
                    ((engagementData?.events?.conversionRate || 0) +
                      (engagementData?.ideas?.interestRate || 0) +
                      (engagementData?.spaces?.conversionRate || 0)) /
                    3
                  ).toFixed(1)}%`}
                  description="Across all features"
                  icon={<TrendingUp className="h-4 w-4" />}
                  variant="cyan"
                />
              </div>

              {/* Events Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-blue-500" />
                    Events Engagement
                  </CardTitle>
                  <CardDescription>Campus events discovery and participation</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-6 grid-cols-1 lg:grid-cols-3">
                    {/* Events Stats */}
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-muted-foreground">Total Events</span>
                          <span className="text-2xl font-bold">{formatNumber(engagementData?.events?.totalViewed || 0)}</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500" style={{ width: '100%' }} />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-muted-foreground">Attendances</span>
                          <span className="text-2xl font-bold text-green-500">{formatNumber(engagementData?.events?.totalAttendances || 0)}</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-500"
                            style={{
                              width: `${Math.min(
                                ((engagementData?.events?.totalAttendances || 0) / Math.max(engagementData?.events?.totalViewed || 1, 1)) * 100,
                                100
                              )}%`,
                            }}
                          />
                        </div>
                      </div>

                      <div className="pt-2 border-t">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Conversion Rate</span>
                          <span className="text-xl font-bold text-purple-500">{engagementData?.events?.conversionRate?.toFixed(1) || 0}%</span>
                        </div>
                      </div>
                    </div>

                    {/* Events Funnel */}
                    <div className="lg:col-span-2">
                      {engagementData?.events && (
                        <EngagementFunnelChart
                          data={[
                            { stage: 'Events Created', count: engagementData?.events?.totalViewed || 0 },
                            { stage: 'Total Attendances', count: engagementData?.events?.totalAttendances || 0 },
                          ]}
                          title=""
                          description=""
                          color="#526ac9"
                        />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Ideas Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-purple-500" />
                    Ideas Engagement
                  </CardTitle>
                  <CardDescription>Student ideas, interests, and collaboration</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-6 grid-cols-1 lg:grid-cols-3">
                    {/* Ideas Stats */}
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-muted-foreground">Total Ideas</span>
                          <span className="text-2xl font-bold">{formatNumber(engagementData?.ideas?.totalViewed || 0)}</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-purple-500" style={{ width: '100%' }} />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-muted-foreground">Interested Users</span>
                          <span className="text-2xl font-bold text-green-500">{formatNumber(engagementData?.ideas?.totalInterested || 0)}</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-500"
                            style={{
                              width: `${Math.min(
                                ((engagementData?.ideas?.totalInterested || 0) / Math.max(engagementData?.ideas?.totalViewed || 1, 1)) * 100,
                                100
                              )}%`,
                            }}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-muted-foreground">Collaborations</span>
                          <span className="text-2xl font-bold text-amber-500">{formatNumber(engagementData?.ideas?.totalCollaborations || 0)}</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-amber-500"
                            style={{
                              width: `${Math.min(
                                ((engagementData?.ideas?.totalCollaborations || 0) / Math.max(engagementData?.ideas?.totalInterested || 1, 1)) * 100,
                                100
                              )}%`,
                            }}
                          />
                        </div>
                      </div>

                      <div className="pt-2 border-t">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Interest Rate</span>
                          <span className="text-xl font-bold text-purple-500">{engagementData?.ideas?.interestRate?.toFixed(1) || 0}%</span>
                        </div>
                      </div>
                    </div>

                    {/* Ideas Funnel */}
                    <div className="lg:col-span-2">
                      {engagementData?.ideas && (
                        <EngagementFunnelChart
                          data={[
                            { stage: 'Ideas Posted', count: engagementData?.ideas?.totalViewed || 0 },
                            { stage: 'Users Interested', count: engagementData?.ideas?.totalInterested || 0 },
                            { stage: 'Collaborations', count: engagementData?.ideas?.totalCollaborations || 0 },
                          ]}
                          title=""
                          description=""
                          color="#8b5cf6"
                        />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Spaces Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-green-500" />
                    Spaces Engagement
                  </CardTitle>
                  <CardDescription>Community spaces and member participation</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-6 grid-cols-1 lg:grid-cols-3">
                    {/* Spaces Stats */}
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-muted-foreground">Total Spaces</span>
                          <span className="text-2xl font-bold">{formatNumber(engagementData?.spaces?.totalViewed || 0)}</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-green-500" style={{ width: '100%' }} />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-muted-foreground">Total Members</span>
                          <span className="text-2xl font-bold text-blue-500">{formatNumber(engagementData?.spaces?.totalJoins || 0)}</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500"
                            style={{
                              width: `${Math.min(
                                ((engagementData?.spaces?.totalJoins || 0) / Math.max(engagementData?.spaces?.totalViewed || 1, 1)) * 100,
                                100
                              )}%`,
                            }}
                          />
                        </div>
                      </div>

                      <div className="pt-2 border-t">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Avg Members/Space</span>
                          <span className="text-xl font-bold text-purple-500">
                            {((engagementData?.spaces?.totalJoins || 0) / Math.max(engagementData?.spaces?.totalViewed || 1, 1)).toFixed(1)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Spaces Funnel */}
                    <div className="lg:col-span-2">
                      {engagementData?.spaces && (
                        <EngagementFunnelChart
                          data={[
                            { stage: 'Spaces Available', count: engagementData?.spaces?.totalViewed || 0 },
                            { stage: 'Total Members', count: engagementData?.spaces?.totalJoins || 0 },
                          ]}
                          title=""
                          description=""
                          color="#10b981"
                        />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Engagement Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>Engagement Summary</CardTitle>
                  <CardDescription>Overall platform engagement across all features</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
                    <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                      <div className="flex items-center gap-3 mb-2">
                        <Activity className="h-5 w-5 text-blue-500" />
                        <h3 className="font-semibold text-blue-700 dark:text-blue-400">Events</h3>
                      </div>
                      <p className="text-2xl font-bold mb-1">{engagementData?.events?.conversionRate?.toFixed(1) || 0}%</p>
                      <p className="text-xs text-muted-foreground">Attendance conversion rate</p>
                    </div>

                    <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
                      <div className="flex items-center gap-3 mb-2">
                        <TrendingUp className="h-5 w-5 text-purple-500" />
                        <h3 className="font-semibold text-purple-700 dark:text-purple-400">Ideas</h3>
                      </div>
                      <p className="text-2xl font-bold mb-1">{engagementData?.ideas?.interestRate?.toFixed(1) || 0}%</p>
                      <p className="text-xs text-muted-foreground">Interest rate</p>
                    </div>

                    <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                      <div className="flex items-center gap-3 mb-2">
                        <Users className="h-5 w-5 text-green-500" />
                        <h3 className="font-semibold text-green-700 dark:text-green-400">Spaces</h3>
                      </div>
                      <p className="text-2xl font-bold mb-1">
                        {((engagementData?.spaces?.totalJoins || 0) / Math.max(engagementData?.spaces?.totalViewed || 1, 1)).toFixed(1)}
                      </p>
                      <p className="text-xs text-muted-foreground">Avg members per space</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Growth Tab - Redesigned */}
            <TabsContent value="growth" className="space-y-6">
              {/* Growth Overview Stats */}
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                <MetricCard
                  title="Total Users"
                  value={formatNumber(socialGraphData?.totalConnections ? Math.round(socialGraphData.totalConnections / (socialGraphData.averageConnectionsPerUser || 1)) : 0)}
                  icon={<Users className="h-4 w-4" />}
                  variant="blue"
                />
                <MetricCard
                  title="New This Week"
                  value={formatNumber(lifecycleData?.newUsers?.total || 0)}
                  description={`${lifecycleData?.newUsers?.firstWeekConnectionRate?.toFixed(0) || 0}% connected`}
                  icon={<UserPlus className="h-4 w-4" />}
                  variant="green"
                />
                <MetricCard
                  title="Total Connections"
                  value={formatNumber(socialGraphData?.totalConnections || 0)}
                  description={`${socialGraphData?.averageConnectionsPerUser?.toFixed(1) || 0} avg/user`}
                  icon={<Activity className="h-4 w-4" />}
                  variant="purple"
                />
                <MetricCard
                  title="Growth Rate"
                  value={`${socialGraphData?.networkGrowthRate?.toFixed(1) || 0}%`}
                  description="Monthly"
                  icon={<TrendingUp className="h-4 w-4" />}
                  variant="cyan"
                />
              </div>

              {/* Growth Charts Row */}
              <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
                {/* User Registrations */}
                {lifecycleData?.registrationsByDate && lifecycleData.registrationsByDate.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <UserPlus className="h-5 w-5 text-blue-500" />
                        User Registrations
                      </CardTitle>
                      <CardDescription>New signups over the last 30 days</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={lifecycleData.registrationsByDate}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 11 }}
                            tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip
                            contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                            labelFormatter={(value) => new Date(value).toLocaleDateString()}
                          />
                          <Line
                            type="monotone"
                            dataKey="registrations"
                            stroke="#526ac9"
                            strokeWidth={2}
                            dot={{ fill: '#526ac9', r: 3 }}
                            name="New Users"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {/* Daily Active Users */}
                {lifecycleData?.activityByDate && lifecycleData.activityByDate.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Activity className="h-5 w-5 text-green-500" />
                        Daily Active Users
                      </CardTitle>
                      <CardDescription>User activity over the last 30 days</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={lifecycleData.activityByDate}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 11 }}
                            tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip
                            contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                            labelFormatter={(value) => new Date(value).toLocaleDateString()}
                          />
                          <Bar
                            dataKey="activeUsers"
                            fill="#10b981"
                            radius={[4, 4, 0, 0]}
                            name="Active Users"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Network & Activation */}
              <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
                {/* Network Growth */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-purple-500" />
                      Network Growth
                    </CardTitle>
                    <CardDescription>Connection and network expansion metrics</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-3 grid-cols-2">
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-muted-foreground">Total Connections</p>
                        <p className="text-2xl font-bold">{formatNumber(socialGraphData?.totalConnections || 0)}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-muted-foreground">Growth Rate</p>
                        <p className="text-2xl font-bold text-green-500">+{socialGraphData?.networkGrowthRate?.toFixed(1) || 0}%</p>
                      </div>
                    </div>
                    {socialGraphData?.connectionDistribution && (
                      <div className="pt-4 border-t">
                        <ConnectionDistributionChart
                          distribution={socialGraphData.connectionDistribution}
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* New User Activation */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <UserPlus className="h-5 w-5 text-cyan-500" />
                      New User Activation
                    </CardTitle>
                    <CardDescription>First-week onboarding success metrics</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid gap-3 grid-cols-3">
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">New Users</p>
                        <p className="text-xl font-bold">{formatNumber(lifecycleData?.newUsers?.total || 0)}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Connected</p>
                        <p className="text-xl font-bold text-green-500">{formatNumber(lifecycleData?.newUsers?.withConnections || 0)}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Success Rate</p>
                        <p className="text-xl font-bold text-purple-500">{lifecycleData?.newUsers?.firstWeekConnectionRate?.toFixed(0) || 0}%</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Connection Progress</span>
                        <span className="font-medium">{lifecycleData?.newUsers?.firstWeekConnectionRate?.toFixed(1) || 0}%</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-cyan-500 to-purple-500 transition-all"
                          style={{ width: `${Math.min(lifecycleData?.newUsers?.firstWeekConnectionRate || 0, 100)}%` }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Viral Growth & Referrals */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-amber-500" />
                    Viral Growth & Referrals
                  </CardTitle>
                  <CardDescription>Organic growth through user referrals and word-of-mouth</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">Total Referred</p>
                      <p className="text-3xl font-bold">{formatNumber(referralsData?.totalReferred || 0)}</p>
                      <p className="text-xs text-muted-foreground">Users who signed up via referral</p>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">Accepted</p>
                      <p className="text-3xl font-bold text-green-500">{formatNumber(referralsData?.totalAccepted || 0)}</p>
                      <p className="text-xs text-muted-foreground">Successful conversions</p>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">Acceptance Rate</p>
                      <p className="text-3xl font-bold text-purple-500">{referralsData?.acceptanceRate?.toFixed(1) || 0}%</p>
                      <p className="text-xs text-muted-foreground">Referral conversion rate</p>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">Viral Coefficient</p>
                      <p className="text-3xl font-bold text-amber-500">{referralsData?.viralCoefficient?.toFixed(2) || "0.00"}</p>
                      <p className="text-xs text-muted-foreground">K-factor (users per user)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Retention & Churn */}
              <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
                {/* Retention Metrics */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-green-500" />
                      User Retention
                    </CardTitle>
                    <CardDescription>Active vs. inactive user breakdown</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid gap-4 grid-cols-2">
                      <div className="space-y-2 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <p className="text-sm font-medium text-amber-700 dark:text-amber-400">Ghost Users</p>
                        <p className="text-2xl font-bold">{formatNumber(lifecycleData?.ghostUsers?.total || 0)}</p>
                        <p className="text-xs text-muted-foreground">{lifecycleData?.ghostUsers?.percentage?.toFixed(1) || 0}% of total</p>
                      </div>
                      <div className="space-y-2 p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                        <p className="text-sm font-medium text-red-700 dark:text-red-400">Churned Users</p>
                        <p className="text-2xl font-bold">{formatNumber(lifecycleData?.churnedUsers?.total || 0)}</p>
                        <p className="text-xs text-muted-foreground">{lifecycleData?.churnedUsers?.churnRate?.toFixed(1) || 0}% churn rate</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Retention Cohort Chart */}
                {lifecycleData?.retentionCohorts && lifecycleData.retentionCohorts.length > 0 ? (
                  <RetentionCohortChart data={lifecycleData.retentionCohorts} />
                ) : (
                  <Card>
                    <CardHeader>
                      <CardTitle>Weekly Retention Cohorts</CardTitle>
                      <CardDescription>User retention by signup week</CardDescription>
                    </CardHeader>
                    <CardContent className="flex items-center justify-center h-48">
                      <p className="text-sm text-muted-foreground">
                        Retention cohort data not available for this period
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Safety Tab */}
            <TabsContent value="safety" className="space-y-6">
              {/* Privacy Adoption */}
              <Card>
                <CardHeader>
                  <CardTitle>Privacy Features</CardTitle>
                  <CardDescription>Location obfuscation adoption</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
                    <MetricCard
                      title="Users with Obfuscation"
                      value={formatNumber(safetyData?.privacyAdoption?.locationObfuscationEnabled || 0)}
                      icon={<Shield className="h-4 w-4" />}
                      variant="green"
                    />
                    <MetricCard
                      title="Adoption Rate"
                      value={`${safetyData?.privacyAdoption?.locationObfuscationPercentage?.toFixed(1) || 0}%`}
                      icon={<TrendingUp className="h-4 w-4" />}
                      variant="purple"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Reports */}
              <Card>
                <CardHeader>
                  <CardTitle>Content Reports</CardTitle>
                  <CardDescription>User-reported content statistics</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
                    <MetricCard
                      title="Total Reports"
                      value={formatNumber(safetyData?.reports?.totalReports || 0)}
                      variant="blue"
                    />
                    <MetricCard
                      title="Resolved"
                      value={formatNumber(safetyData?.reports?.resolved || 0)}
                      variant="green"
                    />
                    <MetricCard
                      title="Pending"
                      value={formatNumber(safetyData?.reports?.pending || 0)}
                      variant="amber"
                    />
                  </div>

                  {/* Reports by Type */}
                  {safetyData?.reports?.byType && Object.keys(safetyData.reports.byType).length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium">Reports by Type</h4>
                      <div className="space-y-2">
                        {Object.entries(safetyData?.reports?.byType || {}).map(([type, count]) => (
                          <DistributionBar
                            key={type}
                            label={type}
                            value={count as number}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Web Analytics Tab - Google Analytics */}
            <TabsContent value="web" className="space-y-6">
              <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Globe className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-semibold">Google Analytics (Web Traffic)</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-6">
                  Insights from your website and web app traffic via Google Analytics
                </p>

                {/* Web Traffic Overview */}
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mb-6">
                  <MetricCard
                    title="Total Users"
                    value={formatNumber(gaUserData?.totalUsers || 0)}
                    description={`${formatNumber(gaUserData?.newUsers || 0)} new users`}
                    icon={<Users className="h-4 w-4" />}
                    variant="blue"
                  />
                  <MetricCard
                    title="Sessions"
                    value={formatNumber(gaSessionData?.sessions || 0)}
                    description={`${gaSessionData?.sessionsPerUser?.toFixed(2) || '0'} per user`}
                    icon={<Activity className="h-4 w-4" />}
                    variant="green"
                  />
                  <MetricCard
                    title="Page Views"
                    value={formatNumber(gaPageData?.pageViews || 0)}
                    description={`Avg ${gaPageData?.averageTimeOnPage?.toFixed(0) || 0}s on page`}
                    icon={<Eye className="h-4 w-4" />}
                    variant="purple"
                  />
                  <MetricCard
                    title="Avg Session Duration"
                    value={`${Math.floor((gaSessionData?.averageSessionDuration || 0) / 60)}m ${Math.floor((gaSessionData?.averageSessionDuration || 0) % 60)}s`}
                    description={`${((gaSessionData?.bounceRate || 0) * 100).toFixed(1)}% bounce rate`}
                    icon={<Clock className="h-4 w-4" />}
                    variant="amber"
                  />
                </div>

                {/* Device Distribution */}
                <div className="grid gap-6 grid-cols-1 lg:grid-cols-2 mb-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Users by Device</CardTitle>
                      <CardDescription>Device type distribution</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {gaUserData?.usersByDevice && gaUserData.usersByDevice.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <PieChart>
                            <Pie
                              data={gaUserData.usersByDevice}
                              dataKey="users"
                              nameKey="deviceCategory"
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              label
                            >
                              {/* Categorical scale, so the hues have to stay apart. The brand purple
                                    sits 30 degrees from the violet that used to be last
                                    here, which put two near-identical slices in a
                                    five-slice pie; that violet is now magenta. */}
                                {gaUserData.usersByDevice.map((_entry, index) => (
                                <Cell key={`cell-${index}`} fill={['#526ac9', '#10b981', '#f59e0b', '#ef4444', '#d946ef'][index % 5]} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-8">
                          No device data available. Make sure Google Analytics is configured.
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Top Countries</CardTitle>
                      <CardDescription>Users by geographic location</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {gaUserData?.usersByCountry && gaUserData.usersByCountry.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={gaUserData.usersByCountry.slice(0, 5)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="country" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="users" fill="#526ac9" />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-8">
                          No country data available
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Sessions by Hour */}
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle>Session Activity by Hour</CardTitle>
                    <CardDescription>Peak traffic hours throughout the day</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {gaSessionData?.sessionsByHour && gaSessionData.sessionsByHour.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={gaSessionData.sessionsByHour}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="hour" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="sessions" stroke="#526ac9" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No hourly session data available
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Top Pages */}
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle>Top Performing Pages</CardTitle>
                    <CardDescription>Most visited pages on your website</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {gaPageData?.topPages && gaPageData.topPages.length > 0 ? (
                      <div className="space-y-4">
                        {gaPageData.topPages.slice(0, 8).map((page, index) => (
                          <div key={index} className="flex items-center justify-between border-b pb-3 last:border-0">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{page.pageTitle || 'Untitled'}</p>
                              <p className="text-xs text-muted-foreground truncate">{page.pagePath}</p>
                            </div>
                            <div className="text-right ml-4">
                              <p className="font-medium text-sm">{formatNumber(page.views)} views</p>
                              <p className="text-xs text-muted-foreground">{page.avgTimeOnPage?.toFixed(0)}s avg</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No page data available
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Traffic Sources */}
                <Card>
                  <CardHeader>
                    <CardTitle>Traffic Sources</CardTitle>
                    <CardDescription>How users are finding your website</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {gaAcquisitionData?.usersBySource && gaAcquisitionData.usersBySource.length > 0 ? (
                      <div className="space-y-4">
                        {gaAcquisitionData.usersBySource.slice(0, 8).map((source, index) => (
                          <div key={index} className="flex items-center justify-between border-b pb-3 last:border-0">
                            <div>
                              <p className="font-medium text-sm">{source.source}</p>
                              <p className="text-xs text-muted-foreground">{source.medium}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-sm">{formatNumber(source.users)} users</p>
                              <p className="text-xs text-muted-foreground">{formatNumber(source.sessions)} sessions</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No traffic source data available
                      </p>
                    )}
                  </CardContent>
                </Card>

                {!gaUserData && !gaSessionData && !gaPageData && (
                  <div className="rounded-lg border border-dashed border-muted-foreground/25 p-8 text-center mt-6">
                    <Monitor className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Google Analytics Not Configured</h3>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      To view web analytics data, please configure Google Analytics in your backend environment variables.
                      See the <code className="text-xs bg-muted px-2 py-1 rounded">GOOGLE_ANALYTICS_SETUP.md</code> file for setup instructions.
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </DashboardLayout>
  );
}

// Helper Components

interface MetricCardProps {
  title: string;
  value: string;
  description?: string;
  icon?: React.ReactNode;
  variant?: 'blue' | 'green' | 'purple' | 'amber' | 'red' | 'cyan';
}

function MetricCard({ title, value, description, icon, variant = 'blue' }: MetricCardProps) {
  const variantClasses = {
    blue: 'bg-blue-500/10 text-blue-500',
    green: 'bg-green-500/10 text-green-500',
    purple: 'bg-purple-500/10 text-purple-500',
    amber: 'bg-amber-500/10 text-amber-500',
    red: 'bg-red-500/10 text-red-500',
    cyan: 'bg-cyan-500/10 text-cyan-500',
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon && <div className={`p-2 rounded-md ${variantClasses[variant]}`}>{icon}</div>}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
      </CardContent>
    </Card>
  );
}

interface DistributionBarProps {
  label: string;
  value: number;
}

function DistributionBar({ label, value }: DistributionBarProps) {
  const maxValue = 1000; // Adjust based on your data

  return (
    <div className="flex items-center gap-4">
      <div className="w-32 text-sm">{label}</div>
      <div className="flex-1">
        <div className="h-6 bg-muted rounded-md overflow-hidden">
          <div
            className="h-full bg-primary/50 transition-all"
            style={{ width: `${Math.min((value / maxValue) * 100, 100)}%` }}
          />
        </div>
      </div>
      <div className="w-16 text-sm text-muted-foreground text-right">
        {formatNumber(value)}
      </div>
    </div>
  );
}
